import React from 'react';
import {
    Box,
    Modal,
    Typography,
    Stack,
    useMediaQuery,
    CircularProgress,
    IconButton,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useResponsiveToast } from '../Toast';
import { ActionButton } from '../Button';
import { fileService } from '../../services/fileService';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

interface FilePreviewDialogProps {
    open: boolean;
    onClose: () => void;
    filePath?: string; // Optional - for fetching files
    fileData?: File | Blob; // Optional - for direct preview
    filename?: string; // Optional - filename for display
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({ 
    open, 
    onClose, 
    filePath, 
    fileData, 
    filename: providedFilename 
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const toast = useResponsiveToast();
    
    // State for file preview
    const [previewFileData, setPreviewFileData] = React.useState<Blob | null>(null);
    const [fileUrl, setFileUrl] = React.useState<string>('');
    const [filename, setFilename] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string>('');

    // Create the default layout plugin
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) => [
            defaultTabs[0], // Thumbnail tab
            defaultTabs[1], // Bookmark tab
        ],
        toolbarPlugin: {
            fullScreenPlugin: {
                onEnterFullScreen: (zoom) => {
                    zoom(1.5);
                    return Promise.resolve();
                },
            },
        },
    });

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: isMobile ? '16px' : '32px',
        boxShadow: 24,
        px: isMobile ? 2 : 4,
        py: isMobile ? 2 : 5,
        width: isMobile ? '85%' : '80%',
        maxWidth: isMobile ? '320px' : '1200px',
        height: isMobile ? '75vh' : '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    // Load file when dialog opens
    React.useEffect(() => {
        if (open) {
            loadFile();
        }
        
        // Cleanup function to revoke blob URL when component unmounts or dialog closes
        return () => {
            if (fileUrl) {
                URL.revokeObjectURL(fileUrl);
            }
        };
    }, [open, filePath, fileData]);

    const loadFile = async () => {
        try {
            setIsLoading(true);
            setError('');
            
            let blobData: Blob;
            let displayFilename: string;

            if (fileData) {
                // Direct preview - use provided file data
                blobData = fileData instanceof File ? fileData : fileData;
                displayFilename = providedFilename || (fileData instanceof File ? fileData.name : 'file');
            } else if (filePath) {
                // Fetch file from path
                const response = await fileService.previewFile(filePath);
                blobData = response.data;
                displayFilename = providedFilename || response.filename || 'file';
            } else {
                throw new Error('No file data or path provided');
            }

            setPreviewFileData(blobData);
            setFilename(displayFilename);
            
            // Create blob URL for preview
            const url = URL.createObjectURL(blobData);
            setFileUrl(url);
        } catch (error: any) {
            console.error('Error loading file:', error);
            setError('Failed to load file. Please try again.');
            toast.error('Failed to load file. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            if (fileData) {
                // Download from file data
                const blob = fileData instanceof File ? fileData : fileData;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('File downloaded successfully!');
            } else if (filePath) {
                // Download using file service
                await fileService.downloadFile(filePath, filename);
                toast.success('File downloaded successfully!');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download file. Please try again.');
        }
    };

    const renderFilePreview = () => {
        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
                    <Stack spacing={isMobile ? 1 : 2} alignItems="center">
                        <CircularProgress size={isMobile ? 40 : 60} sx={{ color: '#132F5F' }} />
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '12px', sm: '14px', md: '16px' }}
                            color='#132F5F'
                        >
                            Loading file...
                        </Typography>
                    </Stack>
                </Box>
            );
        }

        if (error) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
                    <Stack spacing={isMobile ? 1 : 2} alignItems="center">
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '12px', sm: '14px', md: '16px' }}
                            color='#d32f2f'
                            textAlign="center"
                            sx={{ px: isMobile ? 1 : 0 }}
                        >
                            {error}
                        </Typography>
                        <ActionButton
                            onClick={loadFile}
                            width="auto"
                            sx={{
                                height: { xs: 36, sm: 40, md: 48 },
                                fontSize: { xs: '11px', sm: '13px', md: '14px' },
                                px: { xs: 1.5, sm: 2, md: 3 },
                            }}
                        >
                            Retry
                        </ActionButton>
                    </Stack>
                </Box>
            );
        }

        if (!previewFileData || !fileUrl) {
            return null;
        }

        // Determine file type for preview
        const fileType = fileData instanceof File ? fileData.name : filename;
        const isImage = fileService.isImage(fileType);
        const isPDF = fileService.isPDF(fileType);

        // Render based on file type
        if (isImage) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, overflow: 'hidden' }}>
                    <img
                        src={fileUrl}
                        alt={filename}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px',
                        }}
                    />
                </Box>
            );
        }

        if (isPDF) {
            return (
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    borderRadius: isMobile ? '8px' : '8px',
                    border: isMobile ? '1px solid #e0e0e0' : 'none'
                }}>
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer
                            fileUrl={fileUrl}
                            plugins={[defaultLayoutPluginInstance]}
                            theme={{
                                theme: 'auto',
                            }}
                        />
                    </Worker>
                </Box>
            );
        }

        // For other file types, show a generic preview
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
                <Stack spacing={isMobile ? 1 : 2} alignItems="center">
                    <Paper
                        elevation={2}
                        sx={{
                            p: isMobile ? 2 : 4,
                            borderRadius: isMobile ? '12px' : '16px',
                            textAlign: 'center',
                            maxWidth: isMobile ? 250 : 300,
                        }}
                    >
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '12px', sm: '14px', md: '16px' }}
                            color='#132F5F'
                            sx={{ mb: isMobile ? 1 : 2 }}
                        >
                            File Preview Not Available
                        </Typography>
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '10px', sm: '12px', md: '14px' }}
                            color='#666'
                            sx={{ mb: isMobile ? 1 : 2 }}
                        >
                            This file type cannot be previewed in the browser.
                        </Typography>
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '9px', sm: '11px', md: '13px' }}
                            color='#999'
                            sx={{ wordBreak: 'break-word' }}
                        >
                            File: {filename}
                        </Typography>
                    </Paper>
                </Stack>
            </Box>
        );
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                {/* Header - Fixed height */}
                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <VisibilityIcon sx={{ color: '#132F5F', fontSize: { xs: 16, sm: 20, md: 28 } }} />
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontWeight={600}
                            fontSize={{ xs: '12px', sm: '16px', md: '20px' }}
                            lineHeight={{ xs: '16px', sm: '24px', md: '28px' }}
                            color='#132F5F'
                        >
                            File Preview
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ p: isMobile ? 0.5 : 0.5 }}>
                        <CloseIcon sx={{ color: '#132F5F', fontSize: { xs: 18, sm: 22, md: 24 } }} />
                    </IconButton>
                </Stack>

                {/* File Preview - Flexible area */}
                <Box sx={{ flex: 1, mt: isMobile ? 1 : 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {renderFilePreview()}
                </Box>

                {/* Download Button - Fixed height */}
                {previewFileData && !isLoading && !error && (
                    <Box display='flex' justifyContent='center' sx={{ flexShrink: 0, mt: isMobile ? 1 : 2 }}>
                        <ActionButton
                            startIcon={<DownloadIcon sx={{ fontSize: { xs: '10px', sm: '14px', md: '18px' } }} />}
                            onClick={handleDownload}
                            width="auto"
                            sx={{
                                height: { xs: 36, sm: 44, md: 56 },
                                fontSize: { xs: '11px', sm: '13px', md: '16px' },
                                lineHeight: { xs: '16px', sm: '20px', md: '24px' },
                                fontWeight: 500,
                                px: { xs: 1.5, sm: 2, md: 4 },
                                maxWidth: { xs: '100%', sm: 200 },
                                minWidth: { xs: 100, sm: 140, md: 160 },
                            }}
                        >
                            Download File
                        </ActionButton>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default FilePreviewDialog; 